import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FiMail } from 'react-icons/fi';
import { FaInstagram, FaBloggerB, FaTimes } from 'react-icons/fa';
import { BiLink } from 'react-icons/bi';
import { IoPaperPlaneOutline } from 'react-icons/io5';
import { useNoobookContext } from '../context/NoobookContext';
import { FiCamera } from 'react-icons/fi';
import { useUserInfoQuery, useNamecardQuery, getAccessToken, getNativeWebview } from '../react-queries/queries';
import { getMyBookUserStatistics } from '../api/BookcaseApi';
import { processApiResponse } from '../common/errorMessage';
import { RecordType } from '../common/enums';
import { uploadNamecardTempImageFile, editNamecard } from '../api/UserAuthApi';
import { API_URL } from '../config';
import { launchUrl } from '../api/native/NativeApis';

const Wrapper = styled.div`
  background: #fff;
  min-height: calc(100vh - 80px);
  padding: 20px 20px 80px 20px;
`;

const ProfileRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  width: 120px;
  height: 120px;
  position: relative;
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

const UploadPlaceholder = styled.button`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 1px dashed #bdbdbd;
  background: #fafafa;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadBarWrap = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -10px;
  height: 6px;
  background: #eee;
  border-radius: 6px;
  overflow: hidden;
`;
const UploadBar = styled.div`
  height: 100%;
  background: #1976d2;
  width: ${props => props.value || 0}%;
  transition: width 0.2s ease;
`;

const QuoteArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Quote = styled.div`
  font-size: 22px;
  line-height: 1.4;
  color: #222;
  font-weight: 700;
`;

const Nickname = styled.span`
  color: #1976d2;
`;

const MessageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 14px;
`;

const Bio = styled.p`
  margin: 20px 0 0 0;
  color: #333;
  line-height: 1.7;
  font-size: 15px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 0;
  border: 1px solid #eee;
  padding: 18px;
  margin-top: 20px;
`;

const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #222;
  margin-bottom: 12px;
`;

const HashRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px 10px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Hash = styled.span`
  font-size: 15px;
  color: #333;
  white-space: nowrap;
`;

const LinksCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9f9f9;
  border: none;
  padding: 12px 18px;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const LinkRow = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1976d2;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const ActivityCard = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
  background: #fff;
  border: none;
  padding: 0;
  margin-top: 20px;
`;

const Circle = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 10px solid ${props => props.color || '#e5e7eb'};
  position: relative;
`;

const CircleNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222;
`;

const CircleLabel = styled.div`
  text-align: center;
  margin-top: 10px;
  font-size: 13px;
  color: #333;
`;

const CircleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BottomActions = styled.div`
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin: 22px 0 8px 0;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 12px;
  border-radius: 12px;
  border: none;
  background: #fff;
  color: #999;
  font-size: 14px;
  cursor: pointer;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 0 0px 0;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #1976d2;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:active {
    background: #e0e0e0;
  }
`;

const EditModal = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const EditModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 98%;
  height: 98%;
  max-width: 98vw;
  max-height: 98vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EditModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e5e7;
`;

const EditModalTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
`;

const EditModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e5e7;
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e5e7;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const TagInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TagInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TagInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 1px solid #e5e5e7;
  border-radius: 8px;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const TagItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f5f5f7;
  border-radius: 16px;
  font-size: 13px;
  color: #333;
`;

const TagRemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  width: 18px;
  height: 18px;
  
  &:hover {
    color: #333;
  }
`;

const AddTagButton = styled.button`
  padding: 9.7px 16.2px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 11.3px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: #1565c0;
  }
`;

const LinkInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LinkInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const LinkItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f5f5f7;
  border-radius: 8px;
`;

const LinkText = styled.span`
  flex: 1;
  font-size: 13px;
  color: #333;
  word-break: break-all;
`;

const LinkRemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  
  &:hover {
    color: #333;
  }
`;

const EditModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #e5e5e7;
  justify-content: flex-end;
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #1565c0;
  }
  
  &:disabled {
    background: #c7c7cc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f5f5f7;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background: #e5e5e7;
  }
`;

const ModalAvatarWrapper = styled.div`
  width: 100px;
  height: 100px;
  position: relative;
  margin: 0 auto;
`;

const ImageRemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff3b30;
  color: #fff;
  border: 2px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  z-index: 10;
  padding: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: #ff2d20;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModalAvatar = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ModalUploadPlaceholder = styled.button`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 1px dashed #bdbdbd;
  background: #fafafa;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  margin: 0 auto;
`;

export default function NamecardPage() {
  const { setShowBack, setLogoText } = useNoobookContext();
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const { data: userInfo, refetch: refetchUserInfo } = useUserInfoQuery();
  const { data: namecard, refetch: refetchNamecard } = useNamecardQuery();
  const [statistics, setStatistics] = useState({
    doneCnt: 0,
    inProgressCnt: 0,
    memoCnt: 0,
    totalTakenTime: 0
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    introduction: '',
    genreTags: [],
    authorTags: [],
    socialLinks: []
  });
  const [tempGenreTag, setTempGenreTag] = useState('');
  const [tempAuthorTag, setTempAuthorTag] = useState('');
  const [tempSocialLink, setTempSocialLink] = useState('');
  const [modalProfileImage, setModalProfileImage] = useState(null);
  const [modalProfileFile, setModalProfileFile] = useState(null);
  const [originalProfileImage, setOriginalProfileImage] = useState(null);
  const [namecardUrl, setNamecardUrl] = useState(null);
  const modalFileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setShowBack(true);
    setLogoText('');
    return () => {
      setShowBack(false);
      setLogoText('');
    };
  }, [setShowBack, setLogoText]);

  useEffect(() => {
    //const saved = localStorage.getItem('namecard_profile_image');
    //if (saved) {
    //  setProfilePreviewUrl(saved);
    //}
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  // 프로필 이미지 초기화: namecard 또는 userInfo에서 가져오기
  useEffect(() => {
    if (namecard?.imgFileUrl) {
      setProfilePreviewUrl(namecard.imgFileUrl);
    } else if (userInfo?.imgFileUrl) {
      setProfilePreviewUrl(userInfo.imgFileUrl);
    }
    if (namecard?.bookUserStatistics) {
      setStatistics({
        doneCnt: namecard.bookUserStatistics.doneCnt || 0,
        inProgressCnt: namecard.bookUserStatistics.inProgressCnt || 0,
        memoCnt: namecard.bookUserStatistics.memoCnt || 0,
        totalTakenTime: namecard.bookUserStatistics.totalTakenTime || 0
      });
    }
    if (namecard?.uuid) {
      setNamecardUrl(API_URL + '/open/v1/user/namecard/' + namecard.uuid);
    }
  }, [namecard, userInfo]);

  // 통계 데이터 로드
  /*
  const loadStatistics = useCallback(async () => {
    try {
      const { accessToken } = await getAccessToken();
      const res = await getMyBookUserStatistics(accessToken, RecordType.READING);
      const processedResponse = processApiResponse(res);
      
      if (processedResponse.success && processedResponse.data) {
        setStatistics({
          doneCnt: processedResponse.data.doneCnt || 0,
          inProgressCnt: processedResponse.data.inProgressCnt || 0,
          memoCnt: processedResponse.data.memoCnt || 0,
          totalTakenTime: processedResponse.data.totalTakenTime || 0
        });
      }
    } catch (error) {
      console.error('통계 데이터 조회 중 오류 발생:', error);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);
  */
  const handlePickFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleChangeFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    resizeImage(file, 512, 512)
      .then((dataUrl) => {
        if (profilePreviewUrl && profilePreviewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(profilePreviewUrl);
        }
        setProfilePreviewUrl(dataUrl);
        //localStorage.setItem('namecard_profile_image', dataUrl);
      })
      .catch(() => {})
      .finally(() => {
        setIsUploading(false);
        setUploadProgress(0);
      });
  };

  const resizeImage = (file, maxW, maxH) => {
    setIsUploading(true);
    setUploadProgress(10);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        setUploadProgress(30);
        const img = new Image();
        img.onload = async () => {
          setUploadProgress(60);
          let { width, height } = img;
          const scale = Math.min(maxW / width, maxH / height, 1);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          setUploadProgress(85);
          try {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            setUploadProgress(100);
            resolve(dataUrl);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleEdit = () => {
    // 모달 열기 및 초기 데이터 설정
    const originalImage = namecard?.imgFileUrl || userInfo?.imgFileUrl || null;
    setEditFormData({
      title: namecard?.title || '',
      introduction: namecard?.introduction || '',
      genreTags: namecard?.genreTags ? [...namecard.genreTags] : [],
      authorTags: namecard?.authorTags ? [...namecard.authorTags] : [],
      socialLinks: namecard?.socialLinks ? [...namecard.socialLinks] : []
    });
    setModalProfileImage(originalImage);
    setOriginalProfileImage(originalImage);
    setModalProfileFile(null);
    setTempGenreTag('');
    setTempAuthorTag('');
    setTempSocialLink('');
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    if (modalProfileImage && modalProfileImage.startsWith('blob:')) {
      URL.revokeObjectURL(modalProfileImage);
    }
    setModalProfileImage(null);
    setModalProfileFile(null);
    setOriginalProfileImage(null);
  };

  const handleRemoveImage = () => {
    // 변경된 이미지가 blob URL인 경우 메모리 해제
    if (modalProfileImage && modalProfileImage.startsWith('blob:')) {
      URL.revokeObjectURL(modalProfileImage);
    }
    // 원래 이미지로 복원
    setModalProfileImage(originalProfileImage);
    setModalProfileFile(null);
  };

  const handleModalPickFile = () => {
    if (modalFileInputRef.current) modalFileInputRef.current.click();
  };

  const handleModalChangeFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 선택할 수 있습니다.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하만 가능합니다.');
      return;
    }

    resizeImage(file, 512, 512)
      .then((dataUrl) => {
        if (modalProfileImage && modalProfileImage.startsWith('blob:')) {
          URL.revokeObjectURL(modalProfileImage);
        }
        setModalProfileImage(dataUrl);
        setModalProfileFile(file);
      })
      .catch(() => {
        alert('이미지 처리 중 오류가 발생했습니다.');
      });
  };

  const handleAddGenreTag = () => {
    if (tempGenreTag.trim() && !editFormData.genreTags.includes(tempGenreTag.trim())) {
      setEditFormData({
        ...editFormData,
        genreTags: [...editFormData.genreTags, tempGenreTag.trim()]
      });
      setTempGenreTag('');
    }
  };

  const handleRemoveGenreTag = (tag) => {
    setEditFormData({
      ...editFormData,
      genreTags: editFormData.genreTags.filter(t => t !== tag)
    });
  };

  const handleAddAuthorTag = () => {
    if (tempAuthorTag.trim() && !editFormData.authorTags.includes(tempAuthorTag.trim())) {
      setEditFormData({
        ...editFormData,
        authorTags: [...editFormData.authorTags, tempAuthorTag.trim()]
      });
      setTempAuthorTag('');
    }
  };

  const handleRemoveAuthorTag = (tag) => {
    setEditFormData({
      ...editFormData,
      authorTags: editFormData.authorTags.filter(t => t !== tag)
    });
  };

  const handleAddSocialLink = () => {
    if (tempSocialLink.trim() && !editFormData.socialLinks.includes(tempSocialLink.trim())) {
      setEditFormData({
        ...editFormData,
        socialLinks: [...editFormData.socialLinks, tempSocialLink.trim()]
      });
      setTempSocialLink('');
    }
  };

  const handleRemoveSocialLink = (link) => {
    setEditFormData({
      ...editFormData,
      socialLinks: editFormData.socialLinks.filter(l => l !== link)
    });
  };

  const normalizeSocialLinkUrl = (link) => {
    const trimmed = (link || '').trim();
    if (!trimmed) {
      return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return `https://${trimmed}`;
  };

  const handleSocialLinkClick = (e, link) => {
    e.preventDefault();
    const url = normalizeSocialLinkUrl(link);
    if (!url) {
      return;
    }
    if (getNativeWebview()) {
      launchUrl(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyNamecardUrl = async () => {
    if (!namecardUrl) {
      alert('네임카드 URL이 없습니다.먼저 편집을 완료하세요.');
      return;
    }

    try {
      await navigator.clipboard.writeText(namecardUrl);
      alert('네임카드 링크가 클립보드에 복사되었습니다.');
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      // Fallback: 구형 브라우저 지원
      try {
        const textArea = document.createElement('textarea');
        textArea.value = namecardUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('네임카드 링크가 클립보드에 복사되었습니다.');
      } catch (fallbackError) {
        console.error('Fallback 클립보드 복사 실패:', fallbackError);
        alert('클립보드 복사에 실패했습니다.');
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { accessToken } = await getAccessToken();
      
      let imgTmpFileId = null;
      
      // 프로필 이미지 업로드
      if (modalProfileFile) {
        const imageRes = await uploadNamecardTempImageFile(accessToken, modalProfileFile);
        const processedImageResponse = processApiResponse(imageRes);
        if (!processedImageResponse.success) {
          alert(processedImageResponse.errorMessage || '프로필 이미지 업로드에 실패했습니다.');
          setSaving(false);
          return;
        }
        // 반환된 fileId를 imgTmpFileId로 사용 (NoobookFileInfoDTO의 fileId 필드)
        imgTmpFileId = processedImageResponse.data?.fileId || null;
        if (!imgTmpFileId) {
          alert('이미지 파일 ID를 가져오지 못했습니다.');
          setSaving(false);
          return;
        }
      }

      // 네임카드 저장 API 호출
      const namecardEditReqDTO = {
        id: namecard?.id || null,
        imgTmpFileId: imgTmpFileId,
        userId: null,
        uuid: null,
        title: editFormData.title || null,
        introduction: editFormData.introduction || null,
        genreTags: editFormData.genreTags.length > 0 ? editFormData.genreTags : null,
        authorTags: editFormData.authorTags.length > 0 ? editFormData.authorTags : null,
        socialLinks: editFormData.socialLinks.length > 0 ? editFormData.socialLinks : null
      };

      const namecardRes = await editNamecard(accessToken, namecardEditReqDTO);
      const processedNamecardResponse = processApiResponse(namecardRes);
      
      if (!processedNamecardResponse.success) {
        alert(processedNamecardResponse.errorMessage || '네임카드 저장에 실패했습니다.');
        setSaving(false);
        return;
      }
      
      // 성공 시 네임카드 정보 갱신
      await refetchNamecard();
      
      handleCloseEditModal();
    } catch (error) {
      console.error('저장 중 오류 발생:', error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrapper>
      <HeaderRow>
        <EditButton onClick={handleEdit}>편집</EditButton>
      </HeaderRow>
      <ProfileRow>
        <AvatarWrapper>
          {profilePreviewUrl ? (
            <Avatar src={profilePreviewUrl} alt="profile" />
          ) : (
            <UploadPlaceholder onClick={handlePickFile}>
              <FiCamera /> 사진 올리기
            </UploadPlaceholder>
          )}
          {isUploading && (
            <UploadBarWrap>
              <UploadBar value={uploadProgress} />
            </UploadBarWrap>
          )}
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleChangeFile}
          />
        </AvatarWrapper>
        <QuoteArea>
          <Quote>
            {namecard?.title || '-'} <Nickname></Nickname>
          </Quote>
          <MessageRow>
            <FiMail size={18} /> 메시지 보내기
          </MessageRow>
        </QuoteArea>
      </ProfileRow>

      <Bio>
        {namecard?.introduction || '-'}
      </Bio>

      <Card>
        <SectionTitle>좋아하는 장르</SectionTitle>
        <HashRow>
          {namecard?.genreTags && namecard.genreTags.length > 0 ? (
            namecard.genreTags.map((tag, index) => (
              <Hash key={index}>#{tag}</Hash>
            ))
          ) : (
            <>
              <Hash>#</Hash>
            </>
          )}
        </HashRow>
        
        <SectionTitle style={{marginTop: 20}}>선호하는 작가</SectionTitle>
        <HashRow>
          {namecard?.authorTags && namecard.authorTags.length > 0 ? (
            namecard.authorTags.map((tag, index) => (
              <Hash key={index}>#{tag}</Hash>
            ))
          ) : (
            <>
              <Hash>#</Hash>
            </>
          )}
        </HashRow>
        
        <SectionTitle style={{marginTop: 20}}>참여모임</SectionTitle>
        <HashRow>
          {namecard?.bookCommunities && namecard.bookCommunities.length > 0 ? (
            namecard.bookCommunities.map((community, index) => (
              <Hash key={community.bookcommunityId}>#{community.title}</Hash>
            ))
          ) : (
            <>
              <Hash>#</Hash>
            </>
          )}
        </HashRow>
      </Card>

      {namecard?.socialLinks && namecard.socialLinks.length > 0 && (
        <LinksCard>
          {namecard.socialLinks.map((link, index) => {
            // 링크에서 도메인 추출하여 아이콘 결정
            let icon = <BiLink style={{color: '#1976d2', flexShrink: 0}} />;
            if (link.includes('instagram')) {
              icon = <FaInstagram style={{color: '#1976d2', flexShrink: 0}} />;
            } else if (link.includes('blog') || link.includes('naver')) {
              icon = <FaBloggerB style={{color: '#1976d2', flexShrink: 0}} />;
            }
            return (
              <LinkRow
                key={index}
                href={normalizeSocialLinkUrl(link)}
                onClick={(e) => handleSocialLinkClick(e, link)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {icon} {link}
              </LinkRow>
            );
          })}
        </LinksCard>
      )}

      <SectionTitle style={{marginTop: 20}}>누북 활동</SectionTitle>
      <ActivityCard>
        <CircleGroup>
          <Circle color="#245cd8"><CircleNumber>{statistics.doneCnt || 0}</CircleNumber></Circle>
          <CircleLabel>읽은 책</CircleLabel>
        </CircleGroup>
        <CircleGroup>
          <Circle color="#636363"><CircleNumber>{statistics.memoCnt || 0}</CircleNumber></Circle>
          <CircleLabel>쓴 글</CircleLabel>
        </CircleGroup>
        <CircleGroup>
          <Circle color="#ed2f67"><CircleNumber>{statistics.inProgressCnt || 0}</CircleNumber></Circle>
          <CircleLabel>읽는 중</CircleLabel>
        </CircleGroup>
      </ActivityCard>

      <BottomActions>
        <ActionButton onClick={handleCopyNamecardUrl}><BiLink size={20} /> 네임카드 링크 복사</ActionButton>
        {/*<ActionButton><IoPaperPlaneOutline size={20} /> 네임카드 보내기</ActionButton>*/}
      </BottomActions>

      {showEditModal && (
        <EditModal onClick={(e) => e.target === e.currentTarget && handleCloseEditModal()}>
          <EditModalContent onClick={(e) => e.stopPropagation()}>
            <EditModalHeader>
              <EditModalTitle>네임카드 편집</EditModalTitle>
              <CloseButton onClick={handleCloseEditModal}>
                <FaTimes />
              </CloseButton>
            </EditModalHeader>
            <EditModalBody>
              {/* 프로필 이미지 */}
              <FormGroup>
                <FormLabel>프로필 이미지</FormLabel>
                <ModalAvatarWrapper>
                  {modalProfileImage ? (
                    <>
                      <ModalAvatar src={modalProfileImage} alt="profile" onClick={handleModalPickFile} />
                      {modalProfileFile && (
                        <ImageRemoveButton onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}>
                          ×
                        </ImageRemoveButton>
                      )}
                    </>
                  ) : (
                    <ModalUploadPlaceholder onClick={handleModalPickFile}>
                      <FiCamera /> 사진 올리기
                    </ModalUploadPlaceholder>
                  )}
                  <HiddenFileInput
                    ref={modalFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleModalChangeFile}
                  />
                </ModalAvatarWrapper>
              </FormGroup>

              {/* 타이틀 */}
              <FormGroup>
                <FormLabel>타이틀</FormLabel>
                <FormInput
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  placeholder="타이틀을 입력하세요"
                />
              </FormGroup>

              {/* 소개글 */}
              <FormGroup>
                <FormLabel>소개글</FormLabel>
                <FormTextarea
                  value={editFormData.introduction}
                  onChange={(e) => setEditFormData({ ...editFormData, introduction: e.target.value })}
                  placeholder="소개글을 입력하세요"
                />
              </FormGroup>

              {/* 좋아하는 장르 */}
              <FormGroup>
                <FormLabel>좋아하는 장르</FormLabel>
                <TagInputContainer>
                  <TagInputRow>
                    <TagInput
                      type="text"
                      value={tempGenreTag}
                      onChange={(e) => setTempGenreTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGenreTag())}
                      placeholder="장르를 입력하세요"
                    />
                    <AddTagButton onClick={handleAddGenreTag}>추가</AddTagButton>
                  </TagInputRow>
                  {editFormData.genreTags.length > 0 && (
                    <TagList>
                      {editFormData.genreTags.map((tag, index) => (
                        <TagItem key={index}>
                          {tag}
                          <TagRemoveButton onClick={() => handleRemoveGenreTag(tag)}>
                            ×
                          </TagRemoveButton>
                        </TagItem>
                      ))}
                    </TagList>
                  )}
                </TagInputContainer>
              </FormGroup>

              {/* 선호하는 작가 */}
              <FormGroup>
                <FormLabel>선호하는 작가</FormLabel>
                <TagInputContainer>
                  <TagInputRow>
                    <TagInput
                      type="text"
                      value={tempAuthorTag}
                      onChange={(e) => setTempAuthorTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAuthorTag())}
                      placeholder="작가를 입력하세요"
                    />
                    <AddTagButton onClick={handleAddAuthorTag}>추가</AddTagButton>
                  </TagInputRow>
                  {editFormData.authorTags.length > 0 && (
                    <TagList>
                      {editFormData.authorTags.map((tag, index) => (
                        <TagItem key={index}>
                          {tag}
                          <TagRemoveButton onClick={() => handleRemoveAuthorTag(tag)}>
                            ×
                          </TagRemoveButton>
                        </TagItem>
                      ))}
                    </TagList>
                  )}
                </TagInputContainer>
              </FormGroup>

              {/* 소셜 링크 */}
              <FormGroup>
                <FormLabel>소셜 링크</FormLabel>
                <LinkInputContainer>
                  <LinkInputRow>
                    <TagInput
                      type="text"
                      value={tempSocialLink}
                      onChange={(e) => setTempSocialLink(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSocialLink())}
                      placeholder="링크를 입력하세요"
                    />
                    <AddTagButton onClick={handleAddSocialLink}>추가</AddTagButton>
                  </LinkInputRow>
                  {editFormData.socialLinks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                      {editFormData.socialLinks.map((link, index) => (
                        <LinkItem key={index}>
                          <LinkText>{link}</LinkText>
                          <LinkRemoveButton onClick={() => handleRemoveSocialLink(link)}>
                            <FaTimes />
                          </LinkRemoveButton>
                        </LinkItem>
                      ))}
                    </div>
                  )}
                </LinkInputContainer>
              </FormGroup>
            </EditModalBody>
            <EditModalFooter>
              <CancelButton onClick={handleCloseEditModal}>취소</CancelButton>
              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </SaveButton>
            </EditModalFooter>
          </EditModalContent>
        </EditModal>
      )}
    </Wrapper>
  );
}


